import type {AssetId, GroupId} from "../models/GroupRepo";
import type {UploadResponse} from "./upload-file";

const STORE = "assets";
const DB = "liste4000:assets";

const openIndexedDB = async (name: string) => {
  return new Promise<IDBDatabase>((res, rej) => {
    const request = indexedDB.open(name, 1);
    request.onsuccess = () => res(request.result);
    request.onerror = () => rej(request.error ?? new Error("IndexedDB request failed"));
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore(STORE, {keyPath: "id"});
    };
  });
};

let _cachedDb: IDBDatabase | null = null;

const ensureDb = async () => {
  if (_cachedDb) return _cachedDb;
  _cachedDb = await openIndexedDB(DB);
  return _cachedDb;
};

export type LocalAssetEntry = {
  id: AssetId;
  groupId: GroupId;
  mimeType: string;
  fileName: string;
  content: ArrayBuffer;
  uploadResponse: UploadResponse | null;
};

const doOp = async <T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest) => {
  const db = await ensureDb();
  const tx = db.transaction([STORE], mode);
  const store = tx.objectStore(STORE);
  return new Promise<T>((res, rej) => {
    const request = op(store);
    request.onsuccess = () => res(request.result as T);
    request.onerror = () => rej(request.error ?? new Error("IndexedDB request failed"));
  });
};

export const addAssetToLocalStore = async (asset: LocalAssetEntry) => {
  await doOp("readwrite", (store) => store.add(asset));
};

export const getAssetFromLocalStore = async (id: AssetId) => {
  return doOp<LocalAssetEntry | null>("readonly", (store) => store.get(id));
};

export const updateAssetInLocalStore = async (asset: LocalAssetEntry) => {
  await doOp("readwrite", (store) => store.put(asset));
};

export const deleteAssetFromLocalStore = async (id: AssetId) => {
  await doOp("readwrite", (store) => store.delete(id));
};

export const getAllAssetsFromLocalStore = async () => {
  return doOp<LocalAssetEntry[]>("readonly", (store) => store.getAll());
};

// Both the page and the service worker upload pending assets. The lock is shared across
// tabs and the service worker, so only one of them uploads at a time. Callers must re-read
// the local store inside the callback, since the other side may have finished in the meantime.
export const withAssetUploadLock = async <T>(fn: () => Promise<T>): Promise<T> => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!navigator.locks) return fn();
  return navigator.locks.request("liste4000:assetUploads", fn);
};
